<template>
  <div class="min-h-full bg-slate-50/60 dark:bg-[#0B1120] py-6 sm:py-10 px-3 sm:px-6 lg:px-8 font-sans antialiased text-slate-800 dark:text-[#F1F5F9]">
    <!-- Loading Overlay -->
    <AppLoader
      :isVisible="isLoading"
      variant="fullscreen"
      :message="loadingMessage || 'Loading Profile...'"
      subtext="Fetching your credentials and institutional preferences"
      color="indigo"
      icon="cap"
    />

    <div class="max-w-5xl mx-auto space-y-6 sm:space-y-8">
      <!-- ======================================================== -->
      <!-- 1. PROFILE HERO IDENTITY CARD -->
      <!-- ======================================================== -->
      <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-sm overflow-hidden transition-all duration-300">
        <!-- Banner Background with Modern Gradient -->
        <div class="h-32 sm:h-40 bg-gradient-to-r from-blue-700 via-indigo-700 to-slate-900 relative overflow-hidden">
          <div class="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]"></div>
          <!-- Decorative Glow -->
          <div class="absolute -top-12 -right-12 w-48 h-48 bg-blue-400/20 rounded-full blur-2xl"></div>
          <div class="absolute -bottom-8 left-1/3 w-64 h-32 bg-indigo-500/20 rounded-full blur-2xl"></div>
        </div>

        <!-- Identity Bar: Avatar & User Highlights -->
        <div class="px-5 sm:px-8 pb-6 sm:pb-8 pt-0 relative">
          <div class="flex flex-col sm:flex-row sm:items-end justify-between gap-5 -mt-16 sm:-mt-20">
            <!-- Left: Avatar & Names -->
            <div class="flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6 text-center sm:text-left">
              <!-- Avatar Container with Photo Upload -->
              <div class="relative group shrink-0">
                <div class="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl sm:rounded-3xl overflow-hidden ring-4 ring-white dark:ring-[#111C30] shadow-xl bg-slate-100 dark:bg-[#18243A] flex items-center justify-center">
                  <img
                    v-if="photoPreview || displayPhotoUrl"
                    :src="photoPreview || displayPhotoUrl"
                    :alt="currentUser?.name || 'Profile Photo'"
                    class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div
                    v-else
                    class="w-full h-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white font-extrabold text-3xl sm:text-4xl flex items-center justify-center"
                  >
                    {{ userInitials }}
                  </div>
                </div>

                <!-- Camera Upload Button -->
                <label
                  for="profile-photo-upload"
                  class="absolute -bottom-1 -right-1 bg-blue-600 hover:bg-blue-700 text-white p-2.5 rounded-xl cursor-pointer shadow-lg hover:shadow-blue-600/30 ring-4 ring-white dark:ring-[#111C30] transition-all active:scale-95 flex items-center justify-center"
                  title="Upload profile photo"
                >
                  <Camera class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                  <input
                    id="profile-photo-upload"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handlePhotoUpload"
                    :disabled="isLoading"
                  />
                </label>

                <!-- Status dot -->
                <div
                  v-if="currentUser?.status === 'active'"
                  class="absolute top-2 left-2 flex h-3 w-3"
                  title="Account Active"
                >
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 ring-2 ring-white dark:ring-[#111C30]"></span>
                </div>
              </div>

              <!-- Name, Role & Headline -->
              <div class="space-y-1.5 pb-1">
                <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2.5">
                  <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-[#F1F5F9] tracking-tight">
                    {{ currentUser?.name || "User Profile" }}
                  </h1>
                  
                  <!-- Role Tag -->
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1 border"
                    :class="roleBadgeClasses"
                  >
                    <component :is="roleIcon" class="w-3.5 h-3.5" />
                    <span>{{ capitalize(currentUser?.role) || "User" }}</span>
                  </span>
                </div>

                <!-- Secondary Subtitle / Role Context -->
                <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] font-medium flex flex-wrap items-center justify-center sm:justify-start gap-2">
                  <span v-if="currentUser?.role === 'student' && currentUser?.class">
                    Class: <strong class="text-slate-800 dark:text-[#F1F5F9]">{{ currentUser.class }}</strong>
                  </span>
                  <span v-if="currentUser?.role === 'teacher' && currentUser?.subject">
                    Specialization: <strong class="text-slate-800 dark:text-[#F1F5F9]">{{ currentUser.subject }}</strong>
                  </span>
                  <span v-if="currentUser?.schoolId" class="inline-flex items-center gap-1 font-mono text-slate-400 dark:text-[#7E8DA5] text-[11px]">
                    <Shield class="w-3 h-3 text-indigo-500" />
                    ID: {{ currentUser.schoolId }}
                  </span>
                </p>
              </div>
            </div>

            <!-- Right: Action Buttons (Photo upload save & cancel) -->
            <div
              v-if="photoPreview && photoPreview !== displayPhotoUrl"
              class="flex items-center justify-center gap-2 pt-2 sm:pt-0"
            >
              <button
                type="button"
                @click="photoPreview = displayPhotoUrl"
                class="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#18243A] border border-slate-200 dark:border-[#263449] transition cursor-pointer"
                :disabled="isSaving"
              >
                Cancel
              </button>
              <button
                type="button"
                @click="updateProfilePhoto"
                :disabled="isSaving"
                class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-md shadow-blue-500/20 transition cursor-pointer disabled:opacity-50"
              >
                <UploadCloud class="w-4 h-4" />
                <span>{{ isSaving ? "Uploading..." : "Save New Photo" }}</span>
              </button>
            </div>
          </div>

          <!-- Quick Stats / Highlights Strip -->
          <div class="mt-6 pt-6 border-t border-slate-100 dark:border-[#263449] grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div class="space-y-0.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Email Address</span>
              <p class="text-xs sm:text-sm font-semibold text-slate-900 dark:text-[#F1F5F9] truncate" :title="currentUser?.email">
                {{ currentUser?.email || "Not set" }}
              </p>
            </div>

            <div class="space-y-0.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Contact Phone</span>
              <p class="text-xs sm:text-sm font-semibold text-slate-900 dark:text-[#F1F5F9]">
                {{ currentUser?.phone || "Not set" }}
              </p>
            </div>

            <div class="space-y-0.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Account Status</span>
              <div>
                <span
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold capitalize"
                  :class="currentUser?.status === 'active'
                    ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-400'
                    : 'bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-400'"
                >
                  <span class="w-1.5 h-1.5 rounded-full" :class="currentUser?.status === 'active' ? 'bg-emerald-500' : 'bg-rose-500'"></span>
                  {{ currentUser?.status || "Active" }}
                </span>
              </div>
            </div>

            <div class="space-y-0.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">
                {{ currentUser?.role === 'student' ? 'Enrollment' : 'Account Type' }}
              </span>
              <p class="text-xs sm:text-sm font-semibold text-slate-900 dark:text-[#F1F5F9] truncate">
                {{ currentUser?.role === 'student' ? formatDate(currentUser?.enrollmentDate) : capitalize(currentUser?.role) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- 2. NAVIGATION SEGMENTED TABS -->
      <!-- ======================================================== -->
      <div class="flex items-center justify-between border-b border-slate-200 dark:border-[#263449] pb-3">
        <div class="flex items-center gap-2 p-1 bg-slate-200/70 dark:bg-[#18243A] rounded-2xl border border-slate-200 dark:border-[#263449] text-xs font-bold">
          <button
            type="button"
            @click="activeTab = 'overview'"
            class="px-4 py-2 rounded-xl transition cursor-pointer flex items-center gap-2"
            :class="activeTab === 'overview'
              ? 'bg-white dark:bg-[#111C30] text-slate-900 dark:text-[#F1F5F9] shadow-sm'
              : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'"
          >
            <User class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span>Profile Overview</span>
          </button>

          <button
            type="button"
            @click="activeTab = 'security'"
            class="px-4 py-2 rounded-xl transition cursor-pointer flex items-center gap-2"
            :class="activeTab === 'security'
              ? 'bg-white dark:bg-[#111C30] text-slate-900 dark:text-[#F1F5F9] shadow-sm'
              : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]'"
          >
            <Lock class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
            <span>Security & Password</span>
          </button>
        </div>

        <!-- Optional role pill on the right -->
        <span class="hidden md:inline-flex items-center gap-1.5 text-xs text-slate-400 dark:text-[#7E8DA5] font-medium">
          <Sparkles class="w-3.5 h-3.5 text-amber-500" />
          Educanium User ID: {{ currentUser?._id ? currentUser._id.substring(0, 8) + '...' : 'Verified' }}
        </span>
      </div>

      <!-- ======================================================== -->
      <!-- 3. TAB 1: PROFILE OVERVIEW -->
      <!-- ======================================================== -->
      <div v-if="activeTab === 'overview'" class="space-y-6">
        <!-- Admin Missing Contact Alert Banner -->
        <div
          v-if="isVisible"
          class="bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 rounded-2xl p-5 shadow-xs space-y-4"
        >
          <div class="flex items-start gap-3">
            <div class="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/60 text-blue-600 dark:text-blue-300 flex items-center justify-center shrink-0">
              <Info class="w-5 h-5" />
            </div>
            <div>
              <h4 class="text-sm font-bold text-blue-950 dark:text-blue-200">
                Complete Administrator Contact Information
              </h4>
              <p class="text-xs text-blue-800/80 dark:text-blue-300/80 mt-0.5">
                Your profile is missing contact details. Please provide at least an email or phone number to ensure system security notices reach you.
              </p>
            </div>
          </div>

          <form @submit.prevent="saveContact" class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div class="space-y-1.5">
              <label for="admin-email" class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">
                Official Email
              </label>
              <input
                id="admin-email"
                v-model.trim="form.email"
                :disabled="!!currentUser?.email || isSaving"
                placeholder="admin@school.com"
                class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60"
                type="email"
              />
              <p v-if="errors.email" class="text-[11px] text-rose-600 dark:text-rose-400 font-medium">
                {{ errors.email }}
              </p>
            </div>

            <div class="space-y-1.5">
              <label for="admin-phone" class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">
                Phone Number
              </label>
              <input
                id="admin-phone"
                v-model.trim="form.phone"
                :disabled="!!currentUser?.phone || isSaving"
                :placeholder="currentUser?.phone || '9876543210'"
                class="w-full px-3.5 py-2 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60"
                type="tel"
              />
              <p v-if="errors.phone" class="text-[11px] text-rose-600 dark:text-rose-400 font-medium">
                {{ errors.phone }}
              </p>
            </div>

            <div class="sm:col-span-2 flex items-center justify-between pt-1">
              <p v-if="status.message" :class="statusClass" class="text-xs font-semibold">
                {{ status.message }}
              </p>
              <div v-else></div>

              <button
                type="submit"
                :disabled="isSaving"
                class="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-md shadow-blue-600/20 transition active:scale-95 cursor-pointer disabled:opacity-50"
              >
                {{ isSaving ? "Saving..." : "Save Contact Info" }}
              </button>
            </div>
          </form>
        </div>

        <!-- General Contact & Basic Info Card -->
        <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-sm p-6 sm:p-8 space-y-6">
          <div class="flex items-center justify-between border-b border-slate-100 dark:border-[#263449] pb-4">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <Info class="w-4.5 h-4.5" />
              </div>
              <div>
                <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9]">Personal & Contact Information</h3>
                <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">Primary contact details linked to your account.</p>
              </div>
            </div>
          </div>

          <!-- Info Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <!-- Full Name -->
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] flex items-center gap-1.5">
                <User class="w-3 h-3 text-slate-400" />
                Full Name
              </span>
              <p class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">
                {{ currentUser?.name || "N/A" }}
              </p>
            </div>

            <!-- Email Address -->
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] flex items-center gap-1.5">
                <Mail class="w-3 h-3 text-slate-400" />
                Email Address
              </span>
              <p class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9] truncate" :title="currentUser?.email">
                {{ currentUser?.email || "N/A" }}
              </p>
            </div>

            <!-- Phone Number -->
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] flex items-center gap-1.5">
                <Phone class="w-3 h-3 text-slate-400" />
                Phone Number
              </span>
              <p class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">
                {{ currentUser?.phone || "N/A" }}
              </p>
            </div>

            <!-- School ID -->
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] flex items-center gap-1.5">
                <Shield class="w-3 h-3 text-slate-400" />
                School ID / Code
              </span>
              <p class="text-sm font-bold font-mono text-slate-900 dark:text-[#F1F5F9]">
                {{ currentUser?.schoolId || "N/A" }}
              </p>
            </div>

            <!-- Residential Address (if applicable) -->
            <div
              v-if="currentUser?.address"
              class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1 sm:col-span-2"
            >
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] flex items-center gap-1.5">
                <MapPin class="w-3 h-3 text-slate-400" />
                Address
              </span>
              <p class="text-sm font-semibold text-slate-900 dark:text-[#F1F5F9]">
                {{ currentUser.address }}
              </p>
            </div>

            <!-- Remarks (if applicable) -->
            <div
              v-if="currentUser?.remark"
              class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1 sm:col-span-full"
            >
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">
                Remarks & Notes
              </span>
              <p class="text-xs sm:text-sm text-slate-600 dark:text-[#A6B4C8]">
                {{ currentUser.remark }}
              </p>
            </div>
          </div>
        </div>

        <!-- Role Specific: Student Details -->
        <div
          v-if="currentUser?.role === 'student'"
          class="bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-sm p-6 sm:p-8 space-y-6"
        >
          <div class="flex items-center justify-between border-b border-slate-100 dark:border-[#263449] pb-4">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <GraduationCap class="w-4.5 h-4.5" />
              </div>
              <div>
                <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9]">Academic & Roster Record</h3>
                <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">Class, roll allocation, and enrollment records.</p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Assigned Class</span>
              <p class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">{{ currentUser.class || "N/A" }}</p>
            </div>
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Roll Number</span>
              <p class="text-sm font-bold font-mono text-slate-900 dark:text-[#F1F5F9]">{{ currentUser.rollNumber || "N/A" }}</p>
            </div>
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Enrollment Date</span>
              <p class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">{{ formatDate(currentUser.enrollmentDate) }}</p>
            </div>
          </div>

          <!-- Student Documents Strip -->
          <div
            v-if="currentUser.birthCertificate || currentUser.transferCertificate || currentUser.marksheet"
            class="pt-4 space-y-3"
          >
            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] flex items-center gap-1.5">
              <FileText class="w-3.5 h-3.5 text-blue-500" />
              Verified Documents
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              <div
                v-if="currentUser.birthCertificate"
                class="p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235]/50 flex items-center justify-between gap-3"
              >
                <div class="min-w-0">
                  <p class="text-xs font-bold text-slate-900 dark:text-[#F1F5F9] truncate">Birth Certificate</p>
                  <span class="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                    <Check class="w-3 h-3" /> Attached
                  </span>
                </div>
                <a
                  :href="currentUser.birthCertificate"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="p-2 rounded-xl bg-white dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-blue-600 dark:text-blue-400 hover:text-blue-700 transition"
                  title="View Birth Certificate"
                >
                  <ExternalLink class="w-4 h-4" />
                </a>
              </div>

              <div
                v-if="currentUser.transferCertificate"
                class="p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235]/50 flex items-center justify-between gap-3"
              >
                <div class="min-w-0">
                  <p class="text-xs font-bold text-slate-900 dark:text-[#F1F5F9] truncate">Transfer Certificate</p>
                  <span class="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                    <Check class="w-3 h-3" /> Attached
                  </span>
                </div>
                <a
                  :href="currentUser.transferCertificate"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="p-2 rounded-xl bg-white dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-blue-600 dark:text-blue-400 hover:text-blue-700 transition"
                  title="View Transfer Certificate"
                >
                  <ExternalLink class="w-4 h-4" />
                </a>
              </div>

              <div
                v-if="currentUser.marksheet"
                class="p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235]/50 flex items-center justify-between gap-3"
              >
                <div class="min-w-0">
                  <p class="text-xs font-bold text-slate-900 dark:text-[#F1F5F9] truncate">Academic Marksheet</p>
                  <span class="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                    <Check class="w-3 h-3" /> Attached
                  </span>
                </div>
                <a
                  :href="currentUser.marksheet"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="p-2 rounded-xl bg-white dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-blue-600 dark:text-blue-400 hover:text-blue-700 transition"
                  title="View Marksheet"
                >
                  <ExternalLink class="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Role Specific: Teacher Details -->
        <div
          v-else-if="currentUser?.role === 'teacher'"
          class="bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-sm p-6 sm:p-8 space-y-6"
        >
          <div class="flex items-center justify-between border-b border-slate-100 dark:border-[#263449] pb-4">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                <Briefcase class="w-4.5 h-4.5" />
              </div>
              <div>
                <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9]">Faculty & Teaching Profile</h3>
                <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">Subject specialization, identification, and qualifications.</p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Primary Subject</span>
              <p class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">{{ currentUser.subject || "N/A" }}</p>
            </div>
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Date of Birth</span>
              <p class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9]">{{ formatDate(currentUser.dob) }}</p>
            </div>
            <div class="p-4 rounded-2xl bg-slate-50/70 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5]">Aadhaar Identification</span>
              <div class="flex items-center justify-between">
                <p class="text-sm font-bold font-mono text-slate-900 dark:text-[#F1F5F9]">{{ currentUser.aadhaarNumber || "N/A" }}</p>
                <a
                  v-if="currentUser.aadhaarCard"
                  :href="currentUser.aadhaarCard"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold"
                >
                  <Paperclip class="w-3.5 h-3.5" /> View
                </a>
              </div>
            </div>
          </div>

          <!-- Qualifications List -->
          <div
            v-if="currentUser.qualifications && currentUser.qualifications.length > 0"
            class="pt-4 space-y-3"
          >
            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] flex items-center gap-1.5">
              <Award class="w-3.5 h-3.5 text-amber-500" />
              Academic Degrees & Qualifications
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div
                v-for="(q, index) in currentUser.qualifications"
                :key="index"
                class="p-4 rounded-2xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#172235]/50 flex items-center justify-between gap-3"
              >
                <div>
                  <h5 class="text-xs font-bold text-slate-900 dark:text-[#F1F5F9]">{{ q.type || "Qualification" }}</h5>
                  <p class="text-[11px] text-slate-500 dark:text-[#A6B4C8]">{{ q.institution || "Institution" }} · {{ q.year || "Year" }}</p>
                </div>
                <a
                  v-if="q.fileUrl"
                  :href="q.fileUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="p-2 rounded-xl bg-white dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-blue-600 dark:text-blue-400 hover:text-blue-700 transition"
                  title="View Certificate"
                >
                  <ExternalLink class="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Document Modification Notice (for Non-Admins) -->
        <div
          v-if="currentUser?.role !== 'admin'"
          class="bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-900/50 rounded-2xl p-4 flex items-center gap-3"
        >
          <AlertTriangle class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <p class="text-xs text-amber-900 dark:text-amber-300 font-medium leading-relaxed">
            Need to update an official certificate or identification record? Please submit a request to the School Administration office.
          </p>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- 4. TAB 2: SECURITY & CREDENTIALS -->
      <!-- ======================================================== -->
      <div v-else-if="activeTab === 'security'" class="space-y-6">
        <div class="bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/80 dark:border-[#263449] shadow-sm p-6 sm:p-8 space-y-6">
          <div class="flex items-center justify-between border-b border-slate-100 dark:border-[#263449] pb-4">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <Lock class="w-4.5 h-4.5" />
              </div>
              <div>
                <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9]">
                  {{ isInitialPasswordSetup ? "Set Account Password" : "Change Account Password" }}
                </h3>
                <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
                  {{ isInitialPasswordSetup
                    ? "Welcome! Complete your account setup by creating a secure password."
                    : "Update your password periodically to keep your portal account protected." }}
                </p>
              </div>
            </div>
          </div>

          <!-- OTP Request Block (Only for Initial Password Setup) -->
          <div
            v-if="isInitialPasswordSetup"
            class="rounded-2xl border border-blue-200/80 dark:border-blue-900/60 bg-blue-50/60 dark:bg-blue-950/30 p-5 space-y-3"
          >
            <div class="flex flex-col sm:flex-row sm:items-end gap-3">
              <div class="flex-1 space-y-1.5">
                <label for="otp" class="block text-xs font-bold text-blue-950 dark:text-blue-200">
                  One-Time Password (OTP) Verification
                </label>
                <input
                  id="otp"
                  v-model.trim="otp"
                  maxlength="6"
                  inputmode="numeric"
                  placeholder="Enter 6-digit code sent to your phone"
                  class="w-full px-3.5 py-2 text-xs font-mono font-bold tracking-widest rounded-xl border border-blue-200 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-blue-500 outline-none"
                  :disabled="isOtpSending || isSaving || !canSendOtp"
                />
                <p v-if="!canSendOtp" class="text-[11px] text-rose-600 dark:text-rose-400 font-semibold">
                  A registered phone number is required to receive the verification OTP.
                </p>
              </div>

              <button
                type="button"
                @click="sendOtp"
                :disabled="!canSendOtp || isOtpSending || resendSeconds > 0"
                class="px-4 py-2.5 rounded-xl text-white text-xs font-bold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md shadow-blue-500/20"
              >
                <RefreshCw v-if="isOtpSending" class="w-3.5 h-3.5 animate-spin" />
                <span>
                  {{
                    otpSent
                      ? resendSeconds > 0
                        ? `Resend in ${resendSeconds}s`
                        : "Resend Code"
                      : "Send Verification Code"
                  }}
                </span>
              </button>
            </div>

            <p v-if="otpSent" class="text-xs text-blue-700 dark:text-blue-300 font-medium">
              We dispatched an authentication code to your registered mobile device. Code expires in 5 minutes.
            </p>
          </div>

          <!-- Password Form -->
          <form @submit.prevent="changePassword" class="space-y-4 max-w-xl">
            <!-- Current Password (if not initial setup) -->
            <div v-if="!isInitialPasswordSetup" class="space-y-1.5">
              <label for="current-password" class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">
                Current Password
              </label>
              <div class="relative">
                <input
                  id="current-password"
                  :type="currentPasswordType"
                  v-model="passwordForm.currentPassword"
                  :disabled="isLoading"
                  placeholder="••••••••"
                  class="w-full px-3.5 py-2.5 pr-10 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-blue-500 outline-none transition disabled:opacity-50"
                  required
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('current')"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                >
                  <Eye v-if="currentPasswordType === 'password'" class="w-4 h-4" />
                  <EyeOff v-else class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- New Password -->
            <div class="space-y-1.5">
              <label for="new-password" class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">
                New Password
              </label>
              <div class="relative">
                <input
                  id="new-password"
                  :type="newPasswordType"
                  v-model="passwordForm.newPassword"
                  :disabled="isLoading"
                  placeholder="Min. 8 characters with numbers & symbols"
                  class="w-full px-3.5 py-2.5 pr-10 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-blue-500 outline-none transition disabled:opacity-50"
                  required
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('new')"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                >
                  <Eye v-if="newPasswordType === 'password'" class="w-4 h-4" />
                  <EyeOff v-else class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Confirm Password -->
            <div class="space-y-1.5">
              <label for="confirm-password" class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8]">
                Confirm New Password
              </label>
              <div class="relative">
                <input
                  id="confirm-password"
                  :type="confirmPasswordType"
                  v-model="passwordForm.confirmPassword"
                  :disabled="isLoading"
                  placeholder="Re-enter your new password"
                  class="w-full px-3.5 py-2.5 pr-10 text-xs font-semibold rounded-xl border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#172235] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-blue-500 outline-none transition disabled:opacity-50"
                  required
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('confirm')"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                >
                  <Eye v-if="confirmPasswordType === 'password'" class="w-4 h-4" />
                  <EyeOff v-else class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Security Requirements Hints -->
            <div class="p-3.5 rounded-2xl bg-slate-50 dark:bg-[#18243A]/40 border border-slate-200/70 dark:border-[#263449] space-y-1.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] block">
                Password Security Guidelines
              </span>
              <ul class="text-[11px] text-slate-500 dark:text-[#A6B4C8] space-y-0.5 list-disc list-inside">
                <li>At least 6 characters (8+ recommended)</li>
                <li>Includes uppercase, lowercase letters, numbers, and symbols</li>
                <li>Avoid using personal dates or school names</li>
              </ul>
            </div>

            <!-- Submit Button -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="isSaving || (isInitialPasswordSetup && (!otp || otp.length !== 6))"
                class="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-md shadow-blue-500/20 transition active:scale-95 cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                <Lock class="w-3.5 h-3.5" />
                <span>
                  {{ isSaving
                    ? "Updating Credentials..."
                    : isInitialPasswordSetup
                      ? "Set Account Password"
                      : "Update Password" }}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <transition name="fade">
      <div
        v-if="toast.isVisible"
        :class="toastClasses"
        class="fixed bottom-6 right-6 p-4 rounded-2xl shadow-xl text-white text-xs font-bold z-50 flex items-center gap-2 min-w-[260px] border border-white/10"
      >
        <Info v-if="toast.type === 'info'" class="w-4 h-4 shrink-0" />
        <CheckCircle v-if="toast.type === 'success'" class="w-4 h-4 shrink-0" />
        <XCircle v-if="toast.type === 'error'" class="w-4 h-4 shrink-0" />
        <span>{{ toast.message }}</span>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import { ref, computed, reactive, onMounted, watch, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import {
    Camera,
    UploadCloud,
    Paperclip,
    Info,
    CheckCircle,
    XCircle,
    GraduationCap,
    Briefcase,
    ShieldCheck,
    AlertTriangle,
    Lock,
    FileText,
    Award,
    Eye,
    EyeOff,
    User,
    Mail,
    Phone,
    Shield,
    Sparkles,
    Check,
    ExternalLink,
    MapPin,
    RefreshCw,
  } from "lucide-vue-next";

  const store = useStore();

  // Navigation Tabs: 'overview' | 'security'
  const activeTab = ref("overview");

  // State
  const photoPreview = ref(null);
  const selectedPhotoFile = ref(null);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const loadingMessage = ref("Loading profile data...");

  const passwordForm = reactive({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // OTP state for first-time password setup
  const otp = ref("");
  const isOtpSending = ref(false);
  const otpSent = ref(false);
  const resendSeconds = ref(0);
  let resendTimerId = null;

  const form = reactive({
    email: "",
    phone: "",
  });
  const errors = reactive({
    email: "",
    phone: "",
  });
  const status = reactive({
    type: "", // 'success', 'error', 'info'
    message: "",
  });

  // Computed properties from Vuex
  const currentUser = computed(() => store.state.currentUserDetails);

  // Visible only for admin and when either field is missing
  const isVisible = computed(() => {
    const u = currentUser.value;
    return u?.role === "admin" && (!u?.email || !u?.phone);
  });

  const statusClass = computed(() => {
    if (status.type === "error") return "text-rose-600 dark:text-rose-400";
    if (status.type === "success") return "text-emerald-600 dark:text-emerald-400";
    return "text-blue-600 dark:text-blue-400";
  });

  // User Initials
  const userInitials = computed(() => {
    const name = currentUser.value?.name;
    if (!name) return "U";
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  });

  // Dynamic role badge styling
  const roleBadgeClasses = computed(() => {
    const role = currentUser.value?.role;
    if (role === "admin") {
      return "bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/40";
    }
    if (role === "teacher") {
      return "bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/40";
    }
    return "bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40";
  });

  const roleIcon = computed(() => {
    const role = currentUser.value?.role;
    if (role === "admin") return ShieldCheck;
    if (role === "teacher") return Briefcase;
    return GraduationCap;
  });

  // Simple validators
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9+()\-.\s]{7,20}$/;

  function validate() {
    errors.email = "";
    errors.phone = "";
    const u = currentUser.value;

    const wantsToSetEmail = !u?.email && form.email.length > 0;
    const wantsToSetPhone = !u?.phone && form.phone.length > 0;

    if (!wantsToSetEmail && !wantsToSetPhone) {
      status.type = "error";
      status.message = "Please provide at least one contact info to save.";
      return false;
    }

    if (wantsToSetEmail && !emailRegex.test(form.email)) {
      errors.email = "Please enter a valid email.";
      return false;
    }
    if (wantsToSetPhone && !phoneRegex.test(form.phone)) {
      errors.phone = "Please enter a valid phone number.";
      return false;
    }
    return true;
  }

  async function saveContact() {
    status.message = "";
    if (!validate()) return;

    const u = currentUser.value;
    const payload = {};

    if (!u?.email && form.email) payload.email = form.email.trim();
    if (!u?.phone && form.phone) payload.phone = form.phone.trim();

    if (Object.keys(payload).length === 0) {
      status.type = "info";
      status.message = "Nothing to update.";
      return;
    }

    isSaving.value = true;
    try {
      await store.dispatch("updateCurrentUserProfile", payload);
      await store.dispatch("fetchCurrentUser");
      status.type = "success";
      status.message = "Contact info updated successfully.";
      if (payload.email) form.email = "";
      if (payload.phone) form.phone = "";
    } catch (err) {
      const backendMessage = err?.response?.data?.message || err?.message;
      status.type = "error";
      status.message = backendMessage || "Failed to update contact info.";
    } finally {
      isSaving.value = false;
    }
  }

  // Password visibility states
  const currentPasswordType = ref("password");
  const newPasswordType = ref("password");
  const confirmPasswordType = ref("password");

  // Toast
  const toast = reactive({
    isVisible: false,
    message: "",
    type: "info",
  });
  let toastTimeoutId = null;

  // Detect if password is missing (initial setup)
  const isInitialPasswordSetup = computed(
    () => !store.state.currentUserDetails?.hasPassword,
  );

  // Local computed property for displaying photo URL
  const displayPhotoUrl = computed(() => {
    const path = currentUser.value?.photo;
    const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";
    if (!path) return null;
    return path.startsWith("http")
      ? path
      : `${baseURL.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
  });

  const toastClasses = computed(() => {
    const baseClasses = "transition-all duration-300 ease-in-out";
    switch (toast.type) {
      case "success":
        return `${baseClasses} bg-emerald-600`;
      case "error":
        return `${baseClasses} bg-rose-600`;
      case "info":
      default:
        return `${baseClasses} bg-blue-600`;
    }
  });

  const canSendOtp = computed(() => !!currentUser.value?.phone);

  // Utils
  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const showToast = (msg, type = "info", duration = 3000) => {
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    toast.message = msg;
    toast.type = type;
    toast.isVisible = true;
    toastTimeoutId = setTimeout(() => {
      toast.isVisible = false;
    }, duration);
  };

  // Photo upload logic
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      selectedPhotoFile.value = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        photoPreview.value = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      selectedPhotoFile.value = null;
      photoPreview.value = displayPhotoUrl.value;
    }
  };

  // Read file as Base64
  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });

  // Update profile photo
  const updateProfilePhoto = async () => {
    if (!selectedPhotoFile.value || !currentUser.value) {
      showToast("No new photo selected.", "info");
      return;
    }

    isSaving.value = true;
    try {
      const photoBase64 = await readFileAsBase64(selectedPhotoFile.value);
      await store.dispatch("updateCurrentUserProfile", { photoBase64 });
      await store.dispatch("fetchCurrentUser");
      photoPreview.value = null;
      selectedPhotoFile.value = null;
      showToast("Profile photo updated successfully!", "success");
    } catch (error) {
      console.error("Error updating profile photo:", error);
      showToast(error.message || "Failed to update profile photo.", "error");
    } finally {
      isSaving.value = false;
    }
  };

  // Strong password policy: 6+ chars with mixed classes
  const strongPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-={}[\]|:;"'<>,./?]).{6,}$/;

  // Request OTP from backend and start resend cooldown
  const startResendTimer = (seconds = 60) => {
    resendSeconds.value = seconds;
    if (resendTimerId) clearInterval(resendTimerId);
    resendTimerId = setInterval(() => {
      if (resendSeconds.value <= 1) {
        clearInterval(resendTimerId);
        resendTimerId = null;
        resendSeconds.value = 0;
      } else {
        resendSeconds.value -= 1;
      }
    }, 1000);
  };

  const sendOtp = async () => {
    if (!canSendOtp.value) {
      showToast("Please add your phone number first.", "error");
      return;
    }
    if (resendSeconds.value > 0) return;

    isOtpSending.value = true;
    try {
      const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${baseURL.replace(/\/+$/, "")}/api/users/request-password-otp`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to send OTP");
      }

      const data = await res.json();
      otpSent.value = true;
      showToast(
        data.message || "OTP sent successfully. Please check your phone.",
        "success",
      );
      startResendTimer(60);
    } catch (err) {
      showToast(
        err.message || "Unable to send OTP. Please try again.",
        "error",
      );
    } finally {
      isOtpSending.value = false;
    }
  };

  // Change or set password
  const changePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (isInitialPasswordSetup.value) {
      if (!otp.value || otp.value.length !== 6) {
        showToast("Please enter the 6-digit OTP.", "error");
        return;
      }
      if (!newPassword || !confirmPassword) {
        showToast("New password and confirm password are required.", "error");
        return;
      }
      if (newPassword !== confirmPassword) {
        showToast("New password and confirm password do not match.", "error");
        return;
      }
      if (!strongPasswordRegex.test(newPassword)) {
        showToast(
          "Password must be 6+ characters and include uppercase, lowercase, number, and special character.",
          "error",
        );
        return;
      }

      isSaving.value = true;
      try {
        await store.dispatch("updateCurrentUserProfile", {
          newPassword,
          otp: otp.value,
        });
        showToast("Password set successfully!", "success");
        passwordForm.currentPassword = "";
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";
        otp.value = "";
        await store.dispatch("fetchCurrentUser");
      } catch (error) {
        const backendMessage = error?.response?.data?.message;
        showToast(
          backendMessage || error?.message || "Failed to set password.",
          "error",
        );
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";
      } finally {
        isSaving.value = false;
      }
    } else {
      if (!currentPassword || !newPassword || !confirmPassword) {
        showToast("All fields are required.", "error");
        return;
      }
      if (currentPassword === newPassword) {
        showToast(
          "New password cannot be the same as the current password.",
          "error",
        );
        return;
      }
      if (newPassword !== confirmPassword) {
        showToast("New password and confirm password do not match.", "error");
        return;
      }
      if (!strongPasswordRegex.test(newPassword)) {
        showToast(
          "Password must be 6+ characters and include uppercase, lowercase, number, and special character.",
          "error",
        );
        return;
      }

      isLoading.value = true;
      try {
        await store.dispatch("updateCurrentUserProfile", {
          currentPassword,
          newPassword,
        });
        showToast("Password changed successfully!", "success");
        passwordForm.currentPassword = "";
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";
      } catch (error) {
        const backendMessage = error?.response?.data?.message;
        showToast(
          backendMessage ||
            error?.message ||
            "Failed to change password. Please check your current password.",
          "error",
        );
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";
      } finally {
        isLoading.value = false;
      }
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    if (field === "current") {
      currentPasswordType.value =
        currentPasswordType.value === "password" ? "text" : "password";
    } else if (field === "new") {
      newPasswordType.value =
        newPasswordType.value === "password" ? "text" : "password";
    } else if (field === "confirm") {
      confirmPasswordType.value =
        confirmPasswordType.value === "password" ? "text" : "password";
    }
  };

  // Fetch user data on mount
  onMounted(async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchCurrentUser");
    } catch (error) {
      console.error("Error fetching current user:", error);
      showToast("Failed to load profile data.", "error");
    } finally {
      isLoading.value = false;
    }
  });

  // Keep preview in sync
  watch(
    currentUser,
    () => {
      if (!photoPreview.value || photoPreview.value === displayPhotoUrl.value) {
        photoPreview.value = displayPhotoUrl.value || null;
      }
    },
    { immediate: true, deep: true },
  );

  // Reset forms on user update
  watch(
    currentUser,
    (u) => {
      if (!u) return;
      form.email = u.email ? "" : "";
      form.phone = u.phone ? "" : "";
      status.message = "";
      errors.email = "";
      errors.phone = "";
    },
    { immediate: true },
  );

  onUnmounted(() => {
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    if (resendTimerId) clearInterval(resendTimerId);
  });
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(12px);
  }
</style>